import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronRight,
  Home,
  BookOpen,
  MonitorPlay,
  Video,
  FileText,
  BarChart2,
  Activity,
  Radio,
} from "lucide-react";
import { getCoursesById } from "../../../services/course.service";
import { useCourse } from "../../../context/CourseContext";
import { useMeetingsV2 } from "../../../context/MeetingV2Context";
import { useUtilityContext } from "../../../context/UtilityContext";
import {
  pageTitle,
  bodySecondary,
  accentBarSection,
  microLabel,
  card,
  surfaceMuted,
  motionFadeUp,
  motionPageTransition,
  motionHoverLift,
} from "../../../lib/design-system";
import { cn } from "../../../lib/utils";
import { Skeleton, SkeletonCard } from "./ui/Skeleton";

import StudentHome from "../../course/student/course/StudentHome";
import MentorInfo from "../../../components/dashboard/utils/MentorInfo";
import CourseInfo from "../../../components/dashboard/utils/CourseInfo";
import WeeklyPlanTable from "../../../components/dashboard/utils/WeeklyInfo";
import SyllabusAccordion from "../../../components/dashboard/utils/SyllabusComponent";
import LecturePanel from "../../lecture/LecturePanel";
import StudentContentSection from "../../course/student/course/Content/StudentContentSection";
import StudentAssignmentSection from "../../Assignment/student/ShowAssignment";
import StudentActivitySection from "../../Activity/student/StudentActivitySection";
import DiscussionForum from "../../course/teacher/course/Discussion";
import ComingSoonWrapper from "../../../components/ComingSoonWrapper";
import ChatbotWidget from "../../../components/StudentChatbot/ChatbotWidget";
import AllAnnouncements from "../../course/student/course/AllAnnouncements.jsx/AllAnnouncements";

// ---------------------------------------------------------------------------
// NAV CONFIG
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  { key: "Home", label: "Overview", icon: Home },
  { key: "Course", label: "Course Info", icon: BookOpen },
  {
    key: "content",
    label: "Resources",
    icon: MonitorPlay,
    children: [
      { key: "Class Rec.", label: "Recordings", icon: Video },
      { key: "E-Learning", label: "Learning Materials", icon: FileText },
    ],
  },
  {
    key: "assessment",
    label: "Assessments",
    icon: BarChart2,
    children: [
      { key: "Graded", label: "Assignments", icon: FileText },
      { key: "Self Assessment", label: "Self Assessment", icon: FileText, comingSoon: true },
      { key: "Activity", label: "Activities", icon: Activity },
    ],
  },
  { key: "Discussion", label: "Discussions", icon: FileText },
];

// ---------------------------------------------------------------------------
// BREADCRUMB (memoised)
// ---------------------------------------------------------------------------

const Breadcrumb = React.memo(({ courseTitle, onNavigate }) => (
  <motion.div
    {...motionFadeUp}
    className={cn(surfaceMuted, "px-4 sm:px-6 py-3 rounded-sm flex items-center gap-2 text-sm mt-14 lg:mt-0")}
  >
    <button
      onClick={() => onNavigate("Dashboard")}
      className="text-gray-500 dark:text-gray-400 hover:text-accent1 transition-colors font-medium"
    >
      Dashboard
    </button>
    <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
    <button
      onClick={() => onNavigate("Courseware")}
      className="text-gray-500 dark:text-gray-400 hover:text-accent1 transition-colors font-medium"
    >
      Courses
    </button>
    <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
    <span className="text-gray-900 dark:text-white font-bold truncate max-w-[200px] sm:max-w-none">
      {courseTitle}
    </span>
  </motion.div>
));
Breadcrumb.displayName = "Breadcrumb";

// ---------------------------------------------------------------------------
// TAB STRIP (memoised)
// ---------------------------------------------------------------------------

const TabStrip = React.memo(({ activeTab, onTabChange }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (openDropdown) {
        const ref = dropdownRefs.current[openDropdown];
        if (ref && !ref.contains(e.target)) setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openDropdown]);

  const isChildActive = (item) =>
    item.children?.some((c) => c.key === activeTab);

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 relative">
      {/* Desktop tabs */}
      <div className="hidden md:flex items-center gap-1 px-4 sm:px-6">
        {NAV_ITEMS.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isActive = activeTab === item.key || isChildActive(item);

          if (!hasChildren) {
            return (
              <button
                key={item.key}
                onClick={() => onTabChange(item.key)}
                className={cn(
                  "relative px-4 py-3 whitespace-nowrap transition-colors",
                  microLabel,
                  isActive
                    ? "text-accent1"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                )}
              >
                {item.label}
                {isActive && (
                  <motion.div
                    layoutId="inline-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-1 bg-accent1 rounded-sm"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
              </button>
            );
          }

          // Dropdown tab
          return (
            <div
              key={item.key}
              className="relative"
              ref={(el) => (dropdownRefs.current[item.key] = el)}
            >
              <button
                onClick={() =>
                  setOpenDropdown(openDropdown === item.key ? null : item.key)
                }
                className={cn(
                  "relative flex items-center gap-1.5 px-4 py-3 whitespace-nowrap transition-colors",
                  microLabel,
                  isActive
                    ? "text-accent1"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                )}
              >
                {item.label}
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform",
                    openDropdown === item.key && "rotate-180"
                  )}
                />
                {isActive && (
                  <motion.div
                    layoutId="inline-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-1 bg-accent1 rounded-sm"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
              </button>

              {openDropdown === item.key && (
                <div
                  className={cn(
                    card,
                    "absolute left-0 top-full mt-1 w-48 py-1 z-50"
                  )}
                >
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const isComingSoon = child.comingSoon;
                    return (
                      <ComingSoonWrapper
                        key={child.key}
                        isComingSoon={isComingSoon}
                        featureName={child.label}
                        estimatedRelease="Coming Soon"
                        onClick={() => {
                          if (!isComingSoon) {
                            onTabChange(child.key);
                            setOpenDropdown(null);
                          }
                        }}
                      >
                        <button
                          onClick={() => {
                            if (!isComingSoon) {
                              onTabChange(child.key);
                              setOpenDropdown(null);
                            }
                          }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                            activeTab === child.key
                              ? "bg-accent1/10 text-accent1 font-semibold"
                              : cn(
                                  "text-gray-700 dark:text-gray-300 font-medium",
                                  `hover:${surfaceMuted}`
                                ),
                            !isComingSoon && "hover:bg-gray-50 dark:hover:bg-gray-700"
                          )}
                        >
                          <ChildIcon className="h-4 w-4 flex-shrink-0" />
                          {child.label}
                        </button>
                      </ComingSoonWrapper>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile tabs — horizontally scrollable */}
      <div className="md:hidden flex items-center gap-1 px-3 overflow-x-auto scrollbar-hide">
        {NAV_ITEMS.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isActive = activeTab === item.key || isChildActive(item);

          if (!hasChildren) {
            return (
              <button
                key={item.key}
                onClick={() => onTabChange(item.key)}
                className={cn(
                  "relative flex-shrink-0 px-3 py-3 whitespace-nowrap transition-colors text-xs uppercase tracking-wider font-bold",
                  isActive
                    ? "text-accent1"
                    : "text-gray-500 dark:text-gray-400"
                )}
              >
                {item.label}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-accent1 rounded-sm" />
                )}
              </button>
            );
          }

          return (
            <div
              key={item.key}
              className="relative flex-shrink-0"
              ref={(el) => (dropdownRefs.current[`mobile-${item.key}`] = el)}
            >
              <button
                onClick={() =>
                  setOpenDropdown(
                    openDropdown === `mobile-${item.key}` ? null : `mobile-${item.key}`
                  )
                }
                className={cn(
                  "relative flex items-center gap-1 px-3 py-3 whitespace-nowrap transition-colors text-xs uppercase tracking-wider font-bold",
                  isActive
                    ? "text-accent1"
                    : "text-gray-500 dark:text-gray-400"
                )}
              >
                {item.label}
                <ChevronDown
                  className={cn(
                    "h-3 w-3 transition-transform",
                    openDropdown === `mobile-${item.key}` && "rotate-180"
                  )}
                />
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-accent1 rounded-sm" />
                )}
              </button>

              {openDropdown === `mobile-${item.key}` && (
                <div
                  className={cn(
                    card,
                    "absolute left-0 top-full mt-1 w-44 py-1 z-50"
                  )}
                >
                  {item.children.map((child) => {
                    const isComingSoon = child.comingSoon;
                    return (
                      <ComingSoonWrapper
                        key={child.key}
                        isComingSoon={isComingSoon}
                        featureName={child.label}
                        estimatedRelease="Coming Soon"
                        onClick={() => {
                          if (!isComingSoon) {
                            onTabChange(child.key);
                            setOpenDropdown(null);
                          }
                        }}
                      >
                        <button
                          onClick={() => {
                            if (!isComingSoon) {
                              onTabChange(child.key);
                              setOpenDropdown(null);
                            }
                          }}
                          className={cn(
                            "w-full text-left px-4 py-2.5 text-sm transition-colors",
                            activeTab === child.key
                              ? "bg-accent1/10 text-accent1 font-semibold"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          )}
                        >
                          {child.label}
                        </button>
                      </ComingSoonWrapper>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
TabStrip.displayName = "TabStrip";

// ---------------------------------------------------------------------------
// INLINE COURSE SKELETON
// ---------------------------------------------------------------------------

const InlineCourseSkeleton = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="p-4 sm:p-6 space-y-6">
      {/* Breadcrumb */}
      <Skeleton className="h-10 w-full max-w-md" />
      {/* Header */}
      <div className="space-y-3">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-1 w-12" />
      </div>
      {/* Tab strip */}
      <div className="flex gap-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-10 w-24" />
        ))}
      </div>
      {/* Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SkeletonCard className="h-64" />
        <SkeletonCard className="h-64" />
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

const InlineCourseView = ({ initialTab, initialAssignmentId }) => {
  const { selectedCourseId, setActiveSection, activeCourseTab, setActiveCourseTab } = useUtilityContext();
  const { courseData: course, setCourseData } = useCourse();
  const { fetchMeetingsForCourse, getMeetingsForCourse } = useMeetingsV2();

  const [loading, setLoading] = useState(true);
  const activeTab = initialTab || activeCourseTab;
  const [selectedAssignmentId] = useState(initialAssignmentId || null);
  const [fetchedCourseId, setFetchedCourseId] = useState(null);

  // Apply initialTab on first render if provided
  useEffect(() => {
    if (initialTab) {
      setActiveCourseTab(initialTab);
    }
  }, [initialTab, setActiveCourseTab]);

  // Handlers
  const handleBreadcrumbNavigate = useCallback(
    (section) => setActiveSection(section),
    [setActiveSection]
  );

  const handleTabChange = useCallback((tab) => setActiveCourseTab(tab), [setActiveCourseTab]);

  // Fetch course data
  useEffect(() => {
    if (!selectedCourseId || selectedCourseId === fetchedCourseId) return;

    let cancelled = false;
    const fetchCourse = async () => {
      setLoading(true);
      try {
        const response = await getCoursesById(selectedCourseId);
        if (cancelled) return;

        const normalizedSyllabus = (() => {
          if (!response?.syllabus) return { modules: [] };
          let modules = [];
          if (Array.isArray(response.syllabus)) {
            modules = response.syllabus;
          } else if (Array.isArray(response.syllabus.modules)) {
            modules = response.syllabus.modules;
          }
          modules = modules.map((m) => ({
            ...m,
            topics: Array.isArray(m.topics) ? m.topics : [],
          }));
          return { modules };
        })();

        setCourseData({ ...response, syllabus: normalizedSyllabus });
        setFetchedCourseId(selectedCourseId);
      } catch (error) {
        console.error("Error fetching course:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCourse();
    return () => { cancelled = true; };
  }, [selectedCourseId, fetchedCourseId, setCourseData]);

  // Fetch meetings
  useEffect(() => {
    if (selectedCourseId) {
      fetchMeetingsForCourse(selectedCourseId).catch(() => {});
    }
  }, [selectedCourseId, fetchMeetingsForCourse]);

  const courseMeetings = getMeetingsForCourse(selectedCourseId) || [];

  const liveMeeting = useMemo(() => {
    if (!selectedCourseId || !courseMeetings.length) return null;
    return courseMeetings.find(
      (m) => m.course === selectedCourseId && m.status === "live"
    );
  }, [selectedCourseId, courseMeetings]);

  const handleJoinLiveClass = useCallback(() => {
    if (liveMeeting) {
      window.open(liveMeeting.link, "_blank", "noopener,noreferrer");
    }
  }, [liveMeeting]);

  // Render tab content
  const renderContent = () => {
    switch (activeTab) {
      case "Home":
        return <StudentHome setSelectedOption={handleTabChange} courseId={selectedCourseId} />;
      case "Course":
        return (
          <div className=" mx-auto mt-4 px-4 sm:px-6 lg:px-10">
            
            <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 pb-4 sm:pb-6 lg:pb-10">
              <div className="flex flex-col w-full lg:w-[50%] gap-4 sm:gap-6">
                <MentorInfo teacher={course?.teacher} />
                <SyllabusAccordion course={course} />
              </div>
              <div className="w-full lg:w-[50%]">
                <CourseInfo course={course} />
              </div>
            </div>
            <div className="pt-4 sm:pt-6">
              <WeeklyPlanTable course={course} />
            </div>
          </div>
        );
      case "Discussion":
        return <DiscussionForum courseID={selectedCourseId} />;
      case "Class Rec.":
        return <LecturePanel />;
      case "E-Learning":
        return <StudentContentSection />;
      case "Graded":
        return (
          <StudentAssignmentSection
            courseID={selectedCourseId}
            selectedID={selectedAssignmentId || "0"}
          />
        );
      case "Self Assessment":
        return (
          <div className="min-h-[300px] flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl font-semibold text-accent1 mb-2">
                Coming Soon
              </div>
              <div className={bodySecondary}>
                Self-assessment quizzes will be available here shortly.
              </div>
            </div>
          </div>
        );
      case "Activity":
        return (
          <StudentActivitySection courseID={selectedCourseId} selectedID="0" />
        );
      case "Announcements":
        return <AllAnnouncements courseID={selectedCourseId} />;
      default:
        return <StudentHome setSelectedOption={handleTabChange} courseId={selectedCourseId} />;
    }
  };

  if (loading || !course) {
    return <InlineCourseSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Breadcrumb */}
      <div className="px-4 sm:px-6 pt-4">
        <Breadcrumb
          courseTitle={course.title || "Course"}
          onNavigate={handleBreadcrumbNavigate}
        />
      </div>

      {/* Course Header */}
      <motion.div
        {...motionFadeUp}
        className="px-4 sm:px-6 pt-4 pb-2"
      >
        <div className="flex items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className={pageTitle}>{course.title}</h1>
            <p className={cn(bodySecondary, "mt-1")}>
              {course.teacher?.name}
            </p>
            <div className={cn(accentBarSection, "mt-3")} />
          </div>

          {/* Live class button */}
          {liveMeeting ? (
            <motion.button
              whileHover={motionHoverLift}
              onClick={handleJoinLiveClass}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm bg-accent1 text-white font-bold uppercase tracking-wider text-xs shadow-sm hover:shadow-md transition-shadow"
            >
              <Radio className="h-4 w-4 animate-pulse" />
              <span className="hidden sm:inline">Join Live Class</span>
              <span className="sm:hidden">Join Live</span>
            </motion.button>
          ) : (
            <button
              disabled
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider text-xs cursor-not-allowed"
            >
              <Radio className="h-4 w-4" />
              <span className="hidden sm:inline">No Live Class</span>
            </button>
          )}
        </div>
      </motion.div>

      {/* Tab Strip — no overflow hidden so dropdowns render above content */}
      <div className="px-4 sm:px-6 pt-2 relative z-30">
        <TabStrip activeTab={activeTab} onTabChange={handleTabChange} />
      </div>

      {/* Tab Content */}
      <div className="relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            {...motionPageTransition}
            className="py-4"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Chatbot */}
      <ChatbotWidget courseCode={course.courseCode} />
    </div>
  );
};

export default InlineCourseView;
